/**
 * Live-log event types.
 *
 * Two source shapes feed this one type, chosen per role by
 * `RoleSnapshot.schemaVersion` (`src/data/snapshotIndex.ts`):
 *
 * - **v1** (`<version>/log_lines.json`, the real `publish_snapshot` contract
 *   as of 2026-09-26): a plain JSON array of pre-formatted strings —
 *   `organism.observatory.publish._format_log_line`:
 *   "{recorded_time} seq={seq} kind={kind} organism={organism} event_time={event_time}"
 *   — no `knowledge_time`, payload digest, or payload at all, so those
 *   fields are `null`/absent rather than an invented value (never fabricate
 *   data).
 * - **v2** (`events/<from>-<to>.json`, this frontend's incremental-contract
 *   proposal, `src/types/incremental.ts`): the full event shape, payload
 *   included — every field below is populated.
 */

/** One event, from either source shape. */
export interface LogEvent {
  readonly seq: number;
  readonly kind: string;
  readonly organismId: string | null;
  readonly recordedTime: string;
  readonly eventTime: string;
  readonly knowledgeTime: string | null;
  readonly digest: string | null;
  /** Present only for v2-sourced events — the event's full payload, e.g. a
   * `brain.wake` event's `activity_ref`/`final_state_ref`/... . */
  readonly payload?: Readonly<Record<string, unknown>>;
  /** The raw source line, kept for v1 lines that don't match the known
   * format (a future publisher change) so nothing is silently dropped; for
   * v2 events this is a JSON round-trip of the event, mostly for debugging. */
  readonly raw: string;
}

/** Coarse grouping used for filter chips and renderer selection in the log
 * panel (§A.5 "Panel 3 — Live log"). Anything not matched falls into
 * "other" — a real, visible bucket, not a crash. */
export type LogEventGroup =
  | "brain"
  | "portfolio"
  | "life"
  | "family"
  | "market"
  | "order"
  | "lineage"
  | "reconcile"
  | "semantic"
  | "research"
  | "runtime"
  | "observatory"
  | "other";

const GROUP_PREFIXES: readonly (readonly [string, LogEventGroup])[] = [
  ["brain.", "brain"],
  ["portfolio.", "portfolio"],
  ["life.", "life"],
  ["family.", "family"],
  ["lineage.", "lineage"],
  ["market.", "market"],
  ["order.", "order"],
  ["reconcile.", "reconcile"],
  ["semantic.", "semantic"],
  // Owner "resume" message (2026-09-26): tolerate `research.document_ingested`
  // / `research.reflection` — render when present, never crash on them.
  ["research.", "research"],
  ["runtime.", "runtime"],
  ["observatory.", "observatory"],
  ["loop_", "runtime"],
  ["env_", "portfolio"],
  ["calendar.", "market"],
  ["lifecycle.", "life"],
];

export function groupForKind(kind: string): LogEventGroup {
  for (const [prefix, group] of GROUP_PREFIXES) {
    if (kind.startsWith(prefix)) return group;
  }
  return "other";
}
